// Verzeichnis der Einstellungsseiten (Gerüst der Vorarbeit zu Runde 4, S-01). Hier und nirgends sonst steht, welche
// Seiten es gibt, in welcher Gruppe der zweiten Navigationsspalte sie stehen und wer sie sehen darf. Die Routen
// (`/einstellungen/<pfad>`) und die Spalte entstehen daraus; eine neue Seite braucht nur eine Datei in diesem Ordner
// und eine Zeile hier (Runde 8 hängt so Aktivitätstypen, Felder, Status, Nutzer, Vorlagen an).
//
// Besitzer der Seitendateien in Runde 4: Entwickler.tsx und Ereignisse.tsx = Agent 4D, alle anderen unverändert wie
// bisher (Modul oberflaeche). Die Zeile einer Seite ändert nur, wer die Seite besitzt.
// Runde 7 und 8 (Vorarbeit): alle Zeilen der neuen Seiten stehen schon da, jede Seite hat eine Platzhalter-Datei. Besitzer:
// Aktivitaetstypen, Felder = 8A · Status, Pipelines, Links, Organisation = 8B · Nutzer, RollenVerwaltung, Sicherheit = 8C ·
// Textbausteine, Verbrauch, Kontaktregeln, Schalter = 8D · Datenschutz = 7C.
import { lazy, type ComponentType, type LazyExoticComponent } from "react";

export type GruppeId = "allgemein" | "organisation" | "anpassung" | "kommunikation" | "verbinden";

export const GRUPPEN: { id: GruppeId; titel: string }[] = [
  { id: "allgemein", titel: "Allgemein" },
  { id: "organisation", titel: "Organisation" },
  { id: "anpassung", titel: "Anpassen" },
  { id: "kommunikation", titel: "Kommunikation" },
  { id: "verbinden", titel: "Verbinden" },
];

export interface EinstellungsSeite {
  /** Teil der Adresse: /einstellungen/<pfad> */
  pfad: string;
  titel: string;
  untertitel?: string;
  gruppe: GruppeId;
  /** Recht aus `nutzer.permissions`; wer es nicht hat, sieht den Eintrag nicht und wird umgeleitet */
  recht?: string;
  /** Alte Anker der einen langen Seite (`/einstellungen#close` …): führen auf diese Seite */
  anker?: string[];
  komponente: LazyExoticComponent<ComponentType>;
}

export const EINSTELLUNGS_SEITEN: EinstellungsSeite[] = [
  {
    pfad: "konto",
    titel: "Konto",
    untertitel: "Name, Telefonnummer und Passwort.",
    gruppe: "allgemein",
    anker: ["profil", "passwort"],
    komponente: lazy(() => import("./Konto")),
  },
  {
    pfad: "close-schluessel",
    titel: "Close-Schlüssel",
    untertitel: "Mit deinem Schlüssel schreibt das CRM in deinem Namen nach Close.",
    gruppe: "allgemein",
    anker: ["close"],
    komponente: lazy(() => import("./CloseSchluessel")),
  },
  {
    pfad: "darstellung",
    titel: "Darstellung",
    gruppe: "allgemein",
    anker: ["darstellung"],
    komponente: lazy(() => import("./Darstellung")),
  },
  {
    pfad: "tastenkuerzel",
    titel: "Tastenkürzel",
    gruppe: "allgemein",
    anker: ["tasten"],
    komponente: lazy(() => import("./Tastenkuerzel")),
  },
  {
    // Runde 8 (8C): Zwei-Faktor-Anmeldung für das eigene Konto; Organisationsregel für Admins auf derselben Seite
    pfad: "sicherheit",
    titel: "Sicherheit",
    untertitel: "Zwei-Faktor-Anmeldung für dein Konto und, für Admins, für alle.",
    gruppe: "allgemein",
    anker: ["sicherheit"],
    komponente: lazy(() => import("./Sicherheit")),
  },
  {
    pfad: "nutzer",
    titel: "Nutzer",
    untertitel: "Nutzer einladen, Rollen vergeben, deaktivieren und Close-Schlüssel eintragen.",
    gruppe: "organisation",
    recht: "nutzer_verwalten",
    anker: ["nutzer"],
    komponente: lazy(() => import("./Nutzer")),
  },
  {
    // Runde 8 (8C): Rollenkarten, Rechte, Lead-Sichtbarkeit
    pfad: "rollen",
    titel: "Rollen",
    untertitel: "Rechte je Rolle, eigene Rollen und welche Leads wer sieht.",
    gruppe: "organisation",
    recht: "nutzer_verwalten",
    komponente: lazy(() => import("./RollenVerwaltung")),
  },
  {
    // Runde 8 (8B): Name, Währung, Zeitzone, Wochenbeginn, Feiertage
    pfad: "organisation",
    titel: "Organisation",
    untertitel: "Name, Währung, Zeitzone, Wochenbeginn und Feiertage.",
    gruppe: "organisation",
    recht: "manage_organization",
    komponente: lazy(() => import("./Organisation")),
  },
  {
    // Runde 7 (7C): Löschfristen, Löschprotokoll, Auskunft, Werbewidersprüche
    pfad: "datenschutz",
    titel: "Datenschutz",
    untertitel: "Werbewidersprüche, Auskunft und Löschen, Löschfristen und Löschprotokoll.",
    gruppe: "organisation",
    recht: "datenschutz_verwalten",
    komponente: lazy(() => import("./Datenschutz")),
  },
  {
    // Runde 8 (8D): Kosten des Monats, Limit und Warnung
    pfad: "verbrauch",
    titel: "Verbrauch",
    untertitel: "Telefonie-Kosten des Monats, Limit und Warnung.",
    gruppe: "organisation",
    recht: "telefonie_verwalten",
    komponente: lazy(() => import("./Verbrauch")),
  },
  {
    // Runde 8 (8D): Funktionen mit Rechtsstatus ein- und ausschalten
    pfad: "schalter",
    titel: "Funktionsschalter",
    untertitel: "Funktionen mit Rechtsstatus ein- und ausschalten.",
    gruppe: "organisation",
    recht: "manage_organization",
    komponente: lazy(() => import("./Schalter")),
  },
  {
    pfad: "abgleich",
    titel: "Abgleich mit Close",
    untertitel: "Wann die lokale Kopie zuletzt mit Close abgeglichen wurde.",
    gruppe: "organisation",
    anker: ["abgleich"],
    komponente: lazy(() => import("./Abgleich")),
  },
  {
    pfad: "ereignisse",
    titel: "Ereignisse",
    untertitel: "Was im CRM passiert ist: Anmeldungen, Änderungen, Schreibvorgänge nach Close, Lead-Eingang.",
    gruppe: "organisation",
    recht: "ereignisse_lesen",
    komponente: lazy(() => import("./Ereignisse")),
  },
  {
    // Runde 8 (8A): Typen und Felder
    pfad: "aktivitaetstypen",
    titel: "Aktivitätstypen",
    untertitel: "Custom Activities: Typen anlegen, ändern und archivieren, ihre Felder und Pflichtangaben.",
    gruppe: "anpassung",
    recht: "verwaltung_aendern",
    komponente: lazy(() => import("./Aktivitaetstypen")),
  },
  {
    pfad: "felder",
    titel: "Felder",
    untertitel: "Felder an Leads, Kontakten und Opportunities, dazu geteilte Felder.",
    gruppe: "anpassung",
    recht: "verwaltung_aendern",
    komponente: lazy(() => import("./Felder")),
  },
  {
    // Runde 8 (8B): Status, Pipelines, Links
    pfad: "status",
    titel: "Lead-Status",
    untertitel: "Status anlegen, umbenennen, einfärben, ordnen und löschen.",
    gruppe: "anpassung",
    recht: "verwaltung_aendern",
    komponente: lazy(() => import("./Status")),
  },
  {
    pfad: "pipelines",
    titel: "Pipelines",
    untertitel: "Opportunity-Pipelines und ihre Status anlegen, ordnen und löschen.",
    gruppe: "anpassung",
    recht: "verwaltung_aendern",
    komponente: lazy(() => import("./Pipelines")),
  },
  {
    pfad: "links",
    titel: "Integration Links",
    untertitel: "Links an Lead, Kontakt und Opportunity mit Platzhaltern.",
    gruppe: "anpassung",
    recht: "verwaltung_aendern",
    komponente: lazy(() => import("./Links")),
  },
  {
    // Runde 8 (8D): Textbausteine für das Schrägstrich-Menü; jeder Nutzer pflegt seine eigenen
    pfad: "textbausteine",
    titel: "Textbausteine",
    untertitel: "Kurze Texte für das Schrägstrich-Menü in E-Mail, SMS und Notiz.",
    gruppe: "anpassung",
    komponente: lazy(() => import("./Textbausteine")),
  },
  {
    pfad: "email",
    titel: "E-Mail",
    untertitel: "Postfach zum Senden aus dem CRM: Zugang, Absendername und Signatur.",
    gruppe: "kommunikation",
    anker: ["email-konto"],
    komponente: lazy(() => import("./Email")),
  },
  {
    pfad: "vorlagen",
    titel: "Vorlagen",
    untertitel: "Vorlagen für E-Mail und SMS.",
    gruppe: "kommunikation",
    anker: ["vorlagen"],
    komponente: lazy(() => import("./Vorlagen")),
  },
  {
    pfad: "sms",
    titel: "SMS",
    untertitel: "Über welchen Weg SMS gesendet werden.",
    gruppe: "kommunikation",
    anker: ["sms"],
    komponente: lazy(() => import("./Sms")),
  },
  {
    pfad: "telefonie",
    titel: "Telefonie",
    untertitel: "Absendernummer, Handy, Klingeldauer, Aufnahme, Geräte und Leitungstest.",
    gruppe: "kommunikation",
    anker: ["telefonie"],
    komponente: lazy(() => import("./Telefonie")),
  },
  {
    // Runde 6 (6C): Nummern, Mitglieder, Klingelmodus, Sperrliste und Bundles
    pfad: "nummern",
    titel: "Nummern",
    untertitel: "Rufnummern, Mitglieder, Klingelmodus, Sperrliste und Bundle-Status.",
    gruppe: "kommunikation",
    recht: "telefonie_verwalten",
    komponente: lazy(() => import("./Nummern")),
  },
  {
    // Runde 8 (8D): Wählzeiten, Feiertage, Höchstzahl je Lead und Tag, Mindestklingeldauer, Wiederwahlsperre
    pfad: "kontaktregeln",
    titel: "Kontaktregeln",
    untertitel: "Wann und wie oft das CRM anrufen darf.",
    gruppe: "kommunikation",
    recht: "telefonie_verwalten",
    komponente: lazy(() => import("./Kontaktregeln")),
  },
  {
    pfad: "entwickler",
    titel: "Entwickler",
    untertitel: "API-Schlüssel, Lead-Eingang, Webhooks und der Zugang für Claude.",
    gruppe: "verbinden",
    recht: "manage_own_api_keys",
    komponente: lazy(() => import("./Entwickler")),
  },
];

/** Seiten, die dieser Nutzer sehen darf, in der Reihenfolge des Verzeichnisses. */
export function sichtbareSeiten(rechte: readonly string[]): EinstellungsSeite[] {
  return EINSTELLUNGS_SEITEN.filter((s) => !s.recht || rechte.includes(s.recht));
}

/** Seite zu einem alten Anker (`#close` → close-schluessel), sonst undefined. */
export function seiteFuerAnker(anker: string, seiten: EinstellungsSeite[]): EinstellungsSeite | undefined {
  return seiten.find((s) => s.anker?.includes(anker));
}

export const STANDARD_SEITE = "konto";
